import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Icon } from '@vds/icons';
import styled, { keyframes } from 'styled-components';
import { useSelector } from 'react-redux';

import { Flex } from '../../../components/common/StyledComponent/CommonStyledComponent';
import { IconActionButton } from '../AskAIPageStyles';
import AskAIFeedbackPanel from './AskAIFeedbackPanel';
import { decodeHTML } from '../../../utility/utils';
import { FormStart, formComplete, contentClick } from '../../../common/Analytics/GAWrapper';

const messageDropIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-16px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const messageRiseOut = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-16px);
  }
`;

const MESSAGE_ENTRY_DURATION = '700ms';
const MESSAGE_EXIT_DURATION = '480ms';

const SuccessMessage = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-top: 24px;
  padding: 24px;
  border-radius: 8px;
  background: rgba(164, 230, 189, 1);
  animation: ${messageDropIn} ${MESSAGE_ENTRY_DURATION} cubic-bezier(0.22, 1, 0.36, 1) both;

  &.is-closing {
    animation: ${messageRiseOut} ${MESSAGE_EXIT_DURATION} cubic-bezier(0.22, 1, 0.36, 1) both;
  }
`;

const SuccessContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 767px) {
    align-items: flex-start;
  }
`;

const SuccessCloseButton = styled.button`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SuccessIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;

    path {
      stroke: rgba(0, 75, 28, 1);
    }
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-top: 24px;
  padding: 24px;
  border-radius: 8px;
  background: rgba(255, 202, 170, 1);
  animation: ${messageDropIn} ${MESSAGE_ENTRY_DURATION} cubic-bezier(0.22, 1, 0.36, 1) both;

  &.is-closing {
    animation: ${messageRiseOut} ${MESSAGE_EXIT_DURATION} cubic-bezier(0.22, 1, 0.36, 1) both;
  }
`;

const ErrorContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 767px) {
    align-items: flex-start;
  }
`;

const ErrorIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;

    path {
      stroke: rgba(115, 39, 6, 1);
    }
  }
`;

// Helper function to generate unique ID
const generateUniqueId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const AskAIResponseFeedback = ({
  selectedThumb,
  onThumbClick,
  showFeedbackPanel,
  isFeedbackClosing,
  onCloseFeedback,
  onFeedbackAnimationEnd,
  isMobile,
  vegasId,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(true);
  const [isSuccessClosing, setIsSuccessClosing] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isErrorClosing, setIsErrorClosing] = useState(false);
  const askAiData = useSelector((state) => state.Aem?.aemAiSearchData);

  // Track which thumb selection has been tracked to prevent duplicate GA events
  const trackedThumbRef = useRef(null);
  const trackedFormCompleteRef = useRef(false);
  const successCloseButtonRef = useRef(null);
  
  // Generate unique form ID on component mount
  const formId = useMemo(() => generateUniqueId(), []);

  const handleSubmitSuccess = (submittedTags = []) => {
    if (!trackedFormCompleteRef.current && selectedThumb) {
      const formType = selectedThumb === 'up' ? 'Postive_AISearch_Feedback' : 'Negative_AISearch_Feedback';
      const formTopic = Array.isArray(submittedTags) ? submittedTags.join(',') : '';

      formComplete('AISearch_Feedback', '', '', [formId], formType, vegasId, formTopic, false);
      trackedFormCompleteRef.current = true;
    }

    setIsSubmitted(true);
    setShowSuccess(true);
    setIsSuccessClosing(false);
    setShowError(false);
    setIsErrorClosing(false);

    if (typeof onCloseFeedback === 'function') onCloseFeedback();
  };

  const handleSubmitError = () => {
    setIsSubmitted(false);
    setShowSuccess(false);
    setIsSuccessClosing(false);
    setShowError(true);
    setIsErrorClosing(false);

    if (typeof onThumbClick === 'function') onThumbClick(null);
    if (typeof onCloseFeedback === 'function') onCloseFeedback();
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setIsSuccessClosing(true);
  };

  const handleErrorClose = () => {
    setShowError(false);
    setIsErrorClosing(true);
  };

  useEffect(() => {
    if (isSubmitted && showSuccess) {
      successCloseButtonRef.current?.focus();
    }
  }, [isSubmitted, showSuccess]);

  const handleSuccessAnimationEnd = () => {
    if (isSuccessClosing) {
      setShowSuccess(false);
      setIsSuccessClosing(false);
    }
  };

  const handleErrorAnimationEnd = () => {
    if (isErrorClosing) {
      setShowError(false);
      setIsErrorClosing(false);
    }
  };

  // Fire GA event when thumbs are clicked
  const handleThumbClick = (thumbType) => {
    if (isSubmitted) return;
    contentClick(
        thumbType,
        window.location.href,
        '', 
        'feedback_form_thumb_click',
      );
    // Check if this is a new thumb selection (not already tracked)
    if (thumbType && trackedThumbRef.current !== thumbType) {
      // Fire GA event only for new selections
      const formType = thumbType === 'up' ? 'Postive_AISearch_Feedback' : 'Negative_AISearch_Feedback';
      FormStart('AISearch_Feedback', [formId], formType);
      
      // Mark this thumb as tracked
      trackedThumbRef.current = thumbType;
    }

    if (selectedThumb === thumbType) {
      // Deselecting the thumb
      onThumbClick(null);
      setShowSuccess(false);
      trackedThumbRef.current = null;
      if (typeof onCloseFeedback === 'function') onCloseFeedback();
    } else {
      // Selecting the thumb
      onThumbClick(thumbType);
    }
  };

  return (
    <>
      <Flex gap='4px' flexDirection='column'>
        <div className='vds-body-sm'>
          {decodeHTML(askAiData?.resultsPageTexts?.aiNoteDisclaimer)}
        </div>
        <Flex marginTop='16px' gap='12px' marginBottom='12px'>
          <IconActionButton
            type='button'
            disabled={isSubmitted}
            onClick={() => handleThumbClick('up')}
            aria-label='Mark as helpful'
            aria-pressed={selectedThumb === 'up'}
          >
            <Icon
              name={selectedThumb === 'up' ? 'thumbs-up-filled' : 'thumbs-up'}
              size='small'
            />
          </IconActionButton>
          <IconActionButton
            type='button'
            disabled={isSubmitted}
            onClick={() => handleThumbClick('down')}
            aria-label='Mark as not helpful'
            aria-pressed={selectedThumb === 'down'}
          >
            <Icon
              name={selectedThumb === 'down' ? 'thumbs-down-filled' : 'thumbs-down'}
              size='small'
            />
          </IconActionButton>
        </Flex>
        <div className='vds-body-sm'>
          {isSubmitted || Boolean(selectedThumb)
            ? decodeHTML(askAiData?.resultsPageTexts?.thanksFeedBckMsg)
            : decodeHTML(askAiData?.resultsPageTexts?.wasHelpfulMsg)}
        </div>
      </Flex>
      {showFeedbackPanel && !isSubmitted && (
        <AskAIFeedbackPanel
          onClose={onCloseFeedback}
          isClosing={isFeedbackClosing}
          onAnimationEnd={onFeedbackAnimationEnd}
          onSubmit={handleSubmitSuccess}
          onSubmitError={handleSubmitError}
          selectedThumb={selectedThumb}
          vegasId={vegasId}
           formId={formId}
        />
      )}

      {isSubmitted && showSuccess && (
        <SuccessMessage role='alert' aria-atomic='true'
          className={isSuccessClosing ? 'is-closing' : ''}
          onAnimationEnd={handleSuccessAnimationEnd}
        >
          <SuccessContent>
            <SuccessIcon aria-hidden="true">
              <Icon name='checkmark-alt' size={20} color='rgba(0, 75, 28, 1)' />
            </SuccessIcon>
            <div className="vds-body-lg font-eDS-bold" style={{color: '#004B1C'}}>
              {decodeHTML(askAiData?.resultsPageTexts?.feedbackSection?.feedbackSmtMsg)}
            </div>
          </SuccessContent>

          <SuccessCloseButton
            ref={successCloseButtonRef}
            type="button"
            onClick={handleSuccessClose}
            aria-label="Dismiss confirmation"
          >
            <Icon name="close" size={20} color='#004B1C'/>
          </SuccessCloseButton>
        </SuccessMessage>
      )}

      {showError && (
        <ErrorMessage
          className={isErrorClosing ? 'is-closing' : ''}
          onAnimationEnd={handleErrorAnimationEnd}
        >
          <ErrorContent>
            <ErrorIcon aria-hidden="true">
              <Icon name='error' size={20} color='rgba(115, 39, 6, 1)' />
            </ErrorIcon>
            <div className="vds-body-lg font-eDS-bold" style={{ color: 'rgba(115, 39, 6, 1)' }}>
              {decodeHTML(askAiData?.resultsPageTexts?.feedbackSection?.feedBckErrMsg)}
            </div>
          </ErrorContent>

          <SuccessCloseButton
            type="button"
            onClick={handleErrorClose}
            aria-label="Dismiss error"
          >
            <Icon name="close" size={20} color="rgba(115, 39, 6, 1)" />
          </SuccessCloseButton>
        </ErrorMessage>
      )}
    </>
  );
};

export default AskAIResponseFeedback;
